import * as React from "react";

import { getNextRangeSelection } from "@/helpers/rangeSelection";
import { Item } from "@/types/fse";

interface UseItemSelectionProps {
  items: Item[];
  currentPage: number;
  itemsPerPage: number;
  folderId?: string;
  canSelectItem?: (item: Item) => boolean;
}

export const useItemSelection = ({
  items,
  currentPage,
  itemsPerPage,
  folderId,
  canSelectItem,
}: UseItemSelectionProps) => {
  const [selectedItemIds, setSelectedItemIds] = React.useState<Set<string>>(new Set());
  const lastSelectedItemIdRef = React.useRef<string | null>(null);

  const orderedItems = React.useMemo(() => {
    const folders = items.filter(item => item.type === "folder");
    const files = items.filter(item => item.type === "file");
    return [...folders, ...files];
  }, [items]);
  const orderedItemIds = React.useMemo(
    () => orderedItems.filter(item => canSelectItem?.(item) ?? true).map(item => item.id),
    [canSelectItem, orderedItems],
  );
  const selectableIdsSignature = React.useMemo(() => JSON.stringify(orderedItemIds), [orderedItemIds]);

  const handleItemSelection = React.useCallback(
    (itemId: string, isCtrlOrCmd: boolean, isShiftKey?: boolean, targetChecked?: boolean) => {
      if (!orderedItemIds.includes(itemId)) return;
      setSelectedItemIds(prev => {
        if (isShiftKey) {
          return new Set(
            getNextRangeSelection({
              orderedIds: orderedItemIds,
              selectedIds: Array.from(prev),
              anchorId: lastSelectedItemIdRef.current,
              targetId: itemId,
              targetChecked: targetChecked ?? true,
              isRangeSelection: true,
            }),
          );
        }

        const newSelection = new Set(prev);

        if (isCtrlOrCmd) {
          const shouldSelect = targetChecked ?? !newSelection.has(itemId);
          if (shouldSelect) {
            newSelection.add(itemId);
          } else {
            newSelection.delete(itemId);
          }
        } else {
          newSelection.clear();
          newSelection.add(itemId);
        }

        return newSelection;
      });
      lastSelectedItemIdRef.current = itemId;
    },
    [orderedItemIds],
  );

  const clearSelection = React.useCallback(() => {
    lastSelectedItemIdRef.current = null;
    setSelectedItemIds(new Set());
  }, []);

  const handleToggleSelectAll = React.useCallback(() => {
    lastSelectedItemIdRef.current = null;
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedItemIds = orderedItems
      .slice(startIndex, endIndex)
      .filter(item => canSelectItem?.(item) ?? true)
      .map(item => item.id);

    const selectedPaginatedItems = paginatedItemIds.filter(id => selectedItemIds.has(id));

    if (selectedPaginatedItems.length === paginatedItemIds.length) {
      // If all visible items are selected, deselect them
      setSelectedItemIds(prev => {
        const newSelection = new Set(prev);
        paginatedItemIds.forEach(id => newSelection.delete(id));
        return newSelection;
      });
    } else {
      // Otherwise, select all visible items
      setSelectedItemIds(prev => {
        const newSelection = new Set(prev);
        paginatedItemIds.forEach(id => newSelection.add(id));
        return newSelection;
      });
    }
  }, [canSelectItem, orderedItems, selectedItemIds, currentPage, itemsPerPage]);

  // Clear selection when changing directory
  React.useEffect(() => {
    clearSelection();
  }, [folderId, clearSelection]);

  React.useEffect(() => {
    const selectableIds = new Set<string>(JSON.parse(selectableIdsSignature) as string[]);
    setSelectedItemIds(previous => {
      const reconciled = new Set([...previous].filter(id => selectableIds.has(id)));
      return reconciled.size === previous.size ? previous : reconciled;
    });
    if (lastSelectedItemIdRef.current && !selectableIds.has(lastSelectedItemIdRef.current)) {
      lastSelectedItemIdRef.current = null;
    }
  }, [selectableIdsSignature]);

  return {
    selectedItemIds,
    handleItemSelection,
    clearSelection,
    handleToggleSelectAll,
  };
};
